import http from '../../shared/http.js';
import { initFormModalLinks } from '../../shared/formModal.js';
import { withFlash } from '../../shared/flash.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// The two Vermittlungsarten, editable straight from the Stammdaten page.
//
// Each box saves on change rather than through a submit button: it is a single
// boolean, so there is nothing to fill in alongside it and nothing to review
// before sending. No reload either - nothing else on the page reads these two
// fields, so redrawing it would only cost the reader their scroll position.
//
// A failed save puts the box back the way it was. Leaving it on the value the
// user clicked would show a state the server does not have, and the next reload
// would silently contradict it.
//
// The Schichten below them deliberately do *not* work this way: five small chips
// in a row make a misclick both easy to make and easy to miss, and a shift
// flipped by accident is not noticed until a placement goes wrong. They are
// display only, edited through the form.
function initVermittlungsarten() {
  const container = document.getElementById('kunde-vermittlungsarten');
  if (!container) return;

  const { kundeId } = container.dataset;

  container.querySelectorAll('input[type="checkbox"]').forEach((box) => {
    box.addEventListener('change', async () => {
      const feld = box.name;
      const wert = box.checked;

      // Disabled for the round trip, so a double click cannot queue a second
      // request that lands out of order and undoes the first.
      box.disabled = true;

      try {
        await http.patch(`/kunden/${kundeId}`, { [feld]: wert });
        notifySuccess('Vermittlungsarten wurden gespeichert.');
      } catch (err) {
        box.checked = !wert;
        notifyError(
          err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
        );
      } finally {
        box.disabled = false;
      }
    });
  });
}

// The status, edited from the badge that displays it. Everything goes through
// /kunden/:id/status rather than a plain PATCH, because a status change is the
// one edit that writes a Verlauf entry - the endpoint is what guarantees it.
//
// Picking an ordinary status saves straight away. Picking Blacklist opens the
// dialog instead: the schema refuses that status without a reason, so a bare
// pick could only ever come back a 400.
function initStatusFeld() {
  const feld = document.getElementById('kunde-status-feld');
  if (!feld) return;

  const { kundeId } = feld.dataset;
  const badge = document.getElementById('kunde-status-badge');
  const select = document.getElementById('kunde-status-select');
  const modal = document.getElementById('kunde-blacklist-modal');
  const modalForm = document.getElementById('kunde-blacklist-form');

  // The status the record actually carries. Kept here rather than read back off
  // the select, so a cancelled edit can put the control back exactly as it was.
  const aktuell = feld.dataset.status;
  // Set the moment a pick is acted on, so the blur that follows a mouse click
  // does not tear down a control whose save is already under way.
  let laeuft = false;

  const zeigeBadge = () => {
    select.hidden = true;
    select.classList.add('hidden');
    badge.hidden = false;
    select.value = aktuell;
  };

  const zeigeSelect = () => {
    badge.hidden = true;
    select.hidden = false;
    select.classList.remove('hidden');
    select.focus();

    // Drop the list open too, so the badge takes one click rather than two.
    // showPicker() is the only way to open a native select from script, and it
    // throws rather than no-ops where it is unsupported or considers the call
    // untrusted - the focus above is already a usable control, so a failure
    // here costs the user one extra click and nothing else.
    try {
      select.showPicker();
    } catch {
      // Left closed on purpose; the select is focused and keyboard-operable.
    }
  };

  // A status change reloads, unlike the Vermittlungsarten checkboxes beside it:
  // it rewrites the badge, may add the blacklist banner at the top of the page,
  // and always adds an entry to the Verlauf column on the right. Redrawing by
  // hand would mean keeping three regions in step with the server by guesswork.
  const speichern = async (data, ladetext) => {
    laeuft = true;
    showLoading(ladetext);
    try {
      await http.patch(`/kunden/${kundeId}/status`, data);
      window.location.href = withFlash(`/kunden/${kundeId}`, 'kunde-status-geaendert');
    } catch (err) {
      hideLoading();
      laeuft = false;
      zeigeBadge();
      notifyError(
        err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
      );
    }
  };

  badge.addEventListener('click', zeigeSelect);

  select.addEventListener('change', () => {
    const gewaehlt = select.value;

    // Picking the status it already has is not a change; put the badge back
    // rather than sending a request the server would answer as a no-op.
    if (gewaehlt === aktuell) return zeigeBadge();

    if (gewaehlt === 'blacklist') {
      // The dialog carries the reason and the remark. The badge goes back
      // first, so cancelling the dialog leaves the control as it was found.
      zeigeBadge();
      modal.showModal();
      return;
    }

    speichern({ status: gewaehlt }, 'Status wird gesetzt …');
  });

  // Escape and clicking away both abandon the edit. The blur is deferred by a
  // tick because `change` and `blur` both fire when a pick is made with the
  // mouse, in an order browsers disagree on - putting the badge back first
  // would hide the select out from under the handler that is about to save.
  select.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') zeigeBadge();
  });
  select.addEventListener('blur', () => {
    window.setTimeout(() => {
      // document.activeElement still being the select means the blur came from
      // the picker opening rather than from the user leaving: closing up here
      // would shut the list in the same tick it was opened.
      if (laeuft || select.hidden || document.activeElement === select) return;
      zeigeBadge();
    }, 0);
  });

  if (!modalForm) return;

  modalForm.querySelector('[data-action="abbrechen"]')?.addEventListener('click', () => {
    modal.close();
  });

  modalForm.addEventListener('submit', (event) => {
    event.preventDefault();

    const grund = modalForm.querySelector('#blacklist-grund').value;
    const bemerkung = modalForm.querySelector('#blacklist-bemerkung').value.trim();

    // Required by the schema, so it is caught here rather than sent to be
    // refused - the German message is the same one the field would produce.
    if (!grund) return notifyError('Bitte wählen Sie einen Blacklist-Grund.');

    const data = { status: 'blacklist', blacklistGrund: grund };
    if (bemerkung) data.bemerkung = bemerkung;

    modal.close();
    speichern(data, 'Kunde wird auf die Blacklist gesetzt …');
  });
}

// The customer's Stammdaten page: the status badge, the Vermittlungsarten
// checkboxes, and the header's Bearbeiten button.
export function initKundeDetail() {
  // The 'Bearbeiten' button in the header opens the customer's form in the
  // shared dialog rather than navigating to it. A save reloads, since the page
  // is composed server-side.
  //
  // Anchored on /kunden/…/edit rather than any href ending in /edit: the
  // Verlauf column links to note forms whose URLs end the same way, and those
  // belong to kundeVerlauf.js, which refreshes the column instead of reloading.
  initFormModalLinks({
    selector: 'a[href^="/kunden/"][href$="/edit"]:not([href*="/verlauf/"])',
    onSaved: () => window.location.reload()
  });

  // 'Job erstellen' on the Jobs Abschnitt, in the same dialog. Its own call
  // rather than another alternative in the selector above, because the two want
  // different things afterwards: editing the customer redraws this page, where
  // creating a job leaves it for the job that was just created - jobForm.js
  // navigates there itself, so nothing is needed here.
  //
  // The href carries ?kunde=… and openFormModal fetches it verbatim, so the new
  // job's form opens with this customer already chosen and its contacts loaded.
  initFormModalLinks({
    selector: 'a[href^="/jobs/new"]'
  });

  // Above the status form's early return, not below it: both controls live on
  // the Stammdaten page, and a `return` that fires when one is absent would
  // take the other with it.
  initVermittlungsarten();

  initStatusFeld();
}
